import TableHelper from "../utils/TableHelper";
import SubItems from "./SubItems";
import { parsePriceAsString, vatConverter } from "../utils/vatConverter";
import TipsAndTricksDialog from "../base/TipsAndTricksDialog";
import { useState, useCallback } from "react";
import { hasTipsAndTricks } from "../base/TipsAndTricksDialog/TipsAndTricks";
import ProductColumn from "./ProductColumn";
import "./style.css"
import { IColumn, TooltipHost, DefaultButton, Icon, TextField, PrimaryButton, DetailsList, DetailsListLayoutMode, CheckboxVisibility } from "@fluentui/react";
import * as React from "react";
import Api from "../../api";
interface Props {
    debitorNo: string;
    products: Product[];
    addProducts: (products: Product[]) => void;
    changeAmount: (productNo: string, amount: number) => void;
    showStorageWindow: (status: Status[]) => void;
    debitorPriceRequest: (request: ProductPriceRequest[]) => void;
}

const Products: React.FunctionComponent<Props> = props => {
    const [showTipsAndTrickDialog, setShowTipsAndTrickDialog] = useState<
        Product[] | undefined
    >(undefined);

    const addProduct = (product: Product) => () => {
        const { subItems, ...rest } = product;
        let products = [rest];
        if (subItems) {
            const otherProducts: Product[] = subItems.map(sub => ({
                ...sub,
                status: [],
                twoPersonJob: false,
                previouslyPurchased: false
            }));
            products = [...products, ...otherProducts];
        }
        props.addProducts(products);
    };

    const onShowTipsAndTrickClick = useCallback(
        (e: Product) => (event: React.MouseEvent<HTMLElement, MouseEvent>) =>
            setShowTipsAndTrickDialog([e]),
        []
    );

    const onCloseTipsAndTrickDialog = useCallback(
        () => setShowTipsAndTrickDialog(undefined),
        []
    );

    const ClearCache = (itemNumber: string) => async () => {
        await Api.CacheApi.ClearProduct(itemNumber);
        alert(`Cache cleared for item ${itemNumber}`);
    };

    const changeAmount = (product: Product) => (
        e: React.SyntheticEvent<HTMLInputElement>,
        value: string
    ) => props.changeAmount(product.no, parseInt(value, 10));

    const showStorageWindow = (status: Status[]) => () =>
        props.showStorageWindow(status);
   
    const columns: IColumn[] = [
        TableHelper.col(
            "image",
            "",
            46,
            46,
            (e: Product) => ( 
              <Icon title={`Slet cachen for ${e.no}`} iconName="Sync" onClick={ClearCache(e.no)}></Icon>
            ),
            "col-image"
        ),
        TableHelper.col(
            "id",
            "Varenummer",
            16,
            100,
            (e: Product) => {
                if (e.isService) {
                    return null;
                }

                return <ProductColumn product={e} />;
            },
            "col-id"
        ),
        TableHelper.col(
            "name",
            "Navn",
            16,
            220,
            (e: Product) => (
                <TooltipHost
                    content={
                        e.name +
                        (e.description ? " (" + e.description + ")" : "")
                    }
                    id={"name"}
                    calloutProps={{ gapSpace: 0 }}
                >
                    <p>{e.name}</p>
                    {e.customerMessage && (e.bookingAllowed === false || e.mobileServiceAllowed === false) ? (
                        <p className="eurocode__blocked__message">{e.customerMessage}</p>
                    ) : null}
                </TooltipHost>
            ),
            "col-name"
        ),
        TableHelper.col(
            "stock",
            "Lager",
            70,
            70,
            (e: Product) => {
                if (e.isService || e.isVINitem === true) {
                    return null;
                }

                return (
                    <>
                        <label>Lager:</label>
                        <div className="storage_button__container">
                            <DefaultButton
                                text={e.status
                                    .reduce(
                                        (sum, current) => sum + current.amount,
                                        0
                                    )
                                    .toString()}
                                onClick={showStorageWindow(e.status)}
                            />
                        </div>
                    </>
                );
            },
            "col-storage"
        ),
        TableHelper.col(
            "price",
            "Listepris",
            120,
            150,
            (e: Product) => {
                if (e.isVINitem === true) {
                    return null;
                }

                let price = e.price;
                let calibration = null;
                if (e.subItems) {
                    price += e.subItems.reduce(
                        (sum, sub) => sum + sub.itemPrice * sub.amount,
                        0
                    );
                    if (e.subItems.find(x => x.type === 3)) {
                        calibration = (
                            <span className="calibration__icon">
                                <Icon iconName="LocationDot"></Icon>
                            </span>
                        );
                    }
                }
                return (
                    <div className="price__list">
                        <div>
                            <label>Inkl. moms:</label>
                            <p>
                                {parsePriceAsString(price)} {calibration}
                            </p>
                        </div>
                        <div>
                            <label>Eksl. moms:</label>
                            <p>
                                {vatConverter(price)} {calibration}
                            </p>
                        </div>
                    </div>
                );
            },
            "col-price"
        ),

        TableHelper.col(
            "debitorPrice",
            "Kundepris",
            16,
            100,
            (e: Product) => {
                if (e.isVINitem === true) {
                    return null;
                }

                let price = e.discountPrice;
                let calibration = null;
                if (e.subItems) {
                    price += e.subItems.reduce(
                        (sum, sub) => sum + sub.discountPrice * sub.amount,
                        0
                    );
                    if (e.subItems.find(x => x.type === 3)) {
                        calibration = (
                            <span className="calibration__icon">
                                <Icon iconName="LocationDot"></Icon>
                            </span>
                        );
                    }
                }
                return (
                    <div className="price__list">
                        <div>
                            <p>
                                {parsePriceAsString(price)} {calibration}
                            </p>
                        </div>
                        <div>
                            <p>
                                {vatConverter(price)} {calibration}
                            </p>
                        </div>
                    </div>
                );
            },
            "col-deb-price"
        ),
        TableHelper.col(
            "tips",
            "",
            16,
            100,
            (e: Product) => {
                if (e.isVINitem === true) {
                    return null;
                }

                if (hasTipsAndTricks(e)) {
                    return (
                        <Icon
                            aria-valuetext={e.no}
                            onClick={onShowTipsAndTrickClick(e)}
                            iconName="Repair"
                        ></Icon>
                    );
                }
                return <></>;
            },
            "col-deb-price"
        ),
        TableHelper.col(
            "ordernr",
            "",
            150,
            250,
            (e: Product) => {
                if (e.isVINitem === true) {
                    return null;
                }

                let subItem = null;
                if (e.subItems && e.subItems.length > 0) {
                    const { debitorNo, debitorPriceRequest } = props;
                    subItem = (
                        <SubItems
                            storageSum={e.status
                                .reduce(
                                    (sum, current) => sum + current.amount,
                                    0
                                )
                                .toString()}
                            showStorageWindow={showStorageWindow(e.status)}
                            items={[e as SubItem].concat(e.subItems)}
                            productName={e.name}
                            {...{ debitorNo, debitorPriceRequest }}
                        />
                    );
                }
                return (
                    <>
                        {subItem}
                        {e.twoPersonJob ? (
                            <TooltipHost
                                content="To-mands job"
                                id={"tip0"}
                                calloutProps={{ gapSpace: 0 }}
                            >
                                <Icon
                                    title="To-mands job"
                                    style={{
                                        marginLeft: "5px",
                                        marginRight: "10px"
                                    }}
                                    iconName="People"
                                />
                            </TooltipHost>
                        ) : null}
                        {e.previouslyPurchased ? (
                            <TooltipHost
                                content="Purchased"
                                id={"tip1"}
                                calloutProps={{ gapSpace: 0 }}
                            >
                                <Icon
                                    title="Purchased"
                                    style={{
                                        marginLeft: "5px",
                                        marginRight: "10px",
                                        color: "#43a943"
                                    }}
                                    iconName="FavoriteStarFill"
                                />
                            </TooltipHost>
                        ) : null}
                        <TextField
                            style={{ maxWidth: "50px" }}
                            placeholder="Antal"
                            type="number"
                            value={e.amount.toString()}
                            onChange={changeAmount(e)}
                        />
                        <PrimaryButton
                            text={e.amount >= 0 ? "Tilføj" : "Fjern"}
                            onClick={addProduct(e)}
                            disabled={e.bookingAllowed === false}
                        />
                    </>
                );
            },
            "col-buttons"
        )
    ];
    return (
        <>
            <TipsAndTricksDialog
                products={showTipsAndTrickDialog}
                onCloseClick={onCloseTipsAndTrickDialog}
            />
            <DetailsList
                layoutMode={DetailsListLayoutMode.justified}
                isHeaderVisible={true}
                columns={columns}
                items={props.products}
                checkboxVisibility={CheckboxVisibility.hidden}
                className="ProductPriceList"
            />
        </>
    );
};

export default Products;
